import React, { useMemo, useState, useEffect } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { Search, Menu, Keyboard, ChevronRight } from 'lucide-react';
import { useT } from '../i18n';
import { useMobileSidebar } from '../App';
import NotificationBell from './NotificationBell';
import ThemeToggle from './ThemeToggle';
import BRAND from '../brand';
import {
  getBreadcrumbName,
  subscribeBreadcrumbNames,
  breadcrumbNamesVersion,
} from '../lib/breadcrumbNames';

/*
  Shared breadcrumb topbar rendered above every authenticated page so the
  dashboard / campaigns / settings / etc. routes all share the same
  xpnsr.tech crumb + live indicator. The page label is derived from the
  current pathname via react-router.
*/

const PAGE_LABELS_TERMINAL = {
  '/dashboard': 'dashboard',
  '/campaigns': 'campaigns',
  '/landing-pages': 'landing pages',
  '/offers': 'offers',
  '/traffic-sources': 'sources',
  '/traffic': 'fallback rules',
  '/ab-testing': 'a/b tests',
  '/anti-fraud': 'anti-fraud',
  '/analytics': 'analytics',
  '/automation': 'automation',
  '/integrations': 'integrations',
  '/domains': 'domains',
  '/conversion-api': 'conversion api',
  '/api-keys': 'api keys',
  '/workspace': 'workspace',
  '/billing': 'billing',
  '/self-hosted': 'self-hosted',
  '/settings': 'settings',
  '/clicks/log': 'clicks log',
  '/clicks': 'clicks',
  '/marketplace': 'marketplace',
  '/reports/sub-ids': 'sub-ID reports',
  '/campaigns/compare': 'campaign comparison',
};

// Title-case crumbs for brands without the terminal look.
const PAGE_LABELS_TITLE = {
  '/dashboard': 'Dashboard',
  '/campaigns': 'Campaigns',
  '/landing-pages': 'Landing pages',
  '/offers': 'Offers',
  '/traffic-sources': 'Sources',
  '/traffic': 'Fallback rules',
  '/ab-testing': 'A/B tests',
  '/anti-fraud': 'Anti fraud',
  '/analytics': 'Analytics',
  '/automation': 'Automation',
  '/integrations': 'Integrations',
  '/domains': 'Domains',
  '/conversion-api': 'Conversion API',
  '/api-keys': 'API keys',
  '/workspace': 'Workspace',
  '/billing': 'Billing',
  '/settings': 'Settings',
  '/clicks/log': 'Clicks log',
  '/clicks': 'Clicks',
  '/marketplace': 'Marketplace',
  '/reports/sub-ids': 'Sub ID reports',
  '/campaigns/compare': 'Campaign comparison',
};

const PAGE_LABELS = BRAND.terminalLabels ? PAGE_LABELS_TERMINAL : PAGE_LABELS_TITLE;

// A genuine entity id (campaign / offer uuid, hex hash) — truncate those.
const _looksLikeId = (seg) =>
  seg.length >= 12 && /^[0-9a-f][0-9a-f-]*$/i.test(seg) && /[0-9]/.test(seg);
// Turn a plain route word into a readable label ("sub-ids" -> "Sub Ids").
const _humanizeSeg = (seg) =>
  seg.replace(/[-_]+/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

function deriveBreadcrumbs(pathname) {
  // Prefer a page's registered friendly name (dynamic entities), else keep a
  // route word readable, and only ID-truncate things that actually look like
  // ids — a static sub-route like "trends" / "tls" must NOT become "trends…".
  const segLabel = (seg) =>
    getBreadcrumbName(seg) || (_looksLikeId(seg) ? seg.slice(0, 8) + '…' : _humanizeSeg(seg));

  if (PAGE_LABELS[pathname]) return [{ label: PAGE_LABELS[pathname], to: pathname }];

  const match = Object.keys(PAGE_LABELS)
    .filter((p) => p !== '/' && (pathname === p || pathname.startsWith(p + '/')))
    .sort((a, b) => b.length - a.length)[0];

  // Build the crumb trail from the matched base label (when any) plus every
  // remaining path segment, so nested routes uncovered by PAGE_LABELS still
  // get a real breadcrumb instead of nothing.
  const crumbs = [];
  let acc = '';
  if (match) {
    crumbs.push({ label: PAGE_LABELS[match], to: match });
    acc = match;
  }
  const rest = match ? pathname.slice(match.length) : pathname;
  rest
    .split('/')
    .filter(Boolean)
    .forEach((seg) => {
      acc += '/' + seg;
      crumbs.push({ label: segLabel(seg), to: acc });
    });
  return crumbs;
}

export default function XpnsrTopbar() {
  const { t } = useT();
  const location = useLocation();
  // Re-render when a page registers/updates a breadcrumb name so the crumb
  // switches from the raw id to the friendly name once the entity loads.
  const [namesVersion, setNamesVersion] = useState(breadcrumbNamesVersion());
  useEffect(
    () => subscribeBreadcrumbNames(() => setNamesVersion(breadcrumbNamesVersion())),
    [],
  );
  const crumbs = useMemo(
    () => deriveBreadcrumbs(location.pathname),
    [location.pathname, namesVersion],
  );
  const mobileSidebar = useMobileSidebar();

  return (
    <div className="xp-topbar" data-testid="topbar">
      {mobileSidebar && (
        <button
          type="button"
          className="xp-mobile-burger"
          onClick={mobileSidebar.toggle}
          aria-label={t('Toggle navigation')}
          data-testid="mobile-burger"
        >
          <Menu className="w-5 h-5" />
        </button>
      )}
      <div className="xp-crumb">
        {BRAND.terminalLabels ? (
          <>
            <a
              href={BRAND.siteUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="xp-crumb-link"
              data-testid="topbar-crumb-xpnsr"
            >
              xpnsr
            </a>
            {' / '}
            <Link
              to="/dashboard"
              className="xp-crumb-link xp-crumb-link-active"
              data-testid="topbar-crumb-tracker"
            >
              tracker
            </Link>
          </>
        ) : (
          <Link to="/dashboard" className="xp-crumb-link" data-testid="topbar-crumb-tracker">
            {t('Workspace')}
          </Link>
        )}
        {crumbs.map((c, i) => (
          <React.Fragment key={i}>
            <ChevronRight className="inline w-3 h-3 mx-1 opacity-40" />
            {i < crumbs.length - 1 ? (
              <Link to={c.to} className="xp-crumb-link">{c.label}</Link>
            ) : (
              <span className="xp-crumb-current">{c.label}</span>
            )}
          </React.Fragment>
        ))}
      </div>
      <button
        type="button"
        className="xp-topbar-search"
        onClick={() => {
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('xpnsr:open-cmdk'));
          }
        }}
        data-testid="topbar-search-button"
        aria-label={t('Open command palette')}
        style={{ marginLeft: 'auto' }}
      >
        <Search className="xp-topbar-search-icon" />
        <span className="xp-topbar-search-label">{t('Search…')}</span>
        <kbd className="xp-topbar-search-kbd">⌘K</kbd>
      </button>
      <button
        type="button"
        className="xp-topbar-icon-btn"
        onClick={() => window.dispatchEvent(new KeyboardEvent('keydown', { key: '?' }))}
        aria-label={t('Keyboard shortcuts')}
        title={t('Keyboard shortcuts (?)')}
        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', opacity: 0.6, display: 'flex', alignItems: 'center' }}
      >
        <Keyboard style={{ width: 16, height: 16 }} />
      </button>
      <ThemeToggle />
      <NotificationBell />
    </div>
  );
}
